import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import client from "../client";

export type AiSummaryStatus = "fresh" | "stale" | "generating" | "error";

export interface AiSummaryResult {
  status: AiSummaryStatus;
  summaryText: string | null;
  model: string | null;
  computedAt: string | null;
  expiresAt: string | null;
  lastError: string | null;
}

const POLL_INTERVAL_MS = 3_000;
const POLL_CAP_MS = 60_000;

/**
 * Fetches the AI summary for a customer. Polls every 3s while the backend
 * status is 'generating', capped at 60s so a stuck generation doesn't hammer
 * the endpoint indefinitely. Caller controls enabled via customerId.
 */
export function useAiSummary(customerId: string | undefined) {
  return useQuery<AiSummaryResult>({
    queryKey: ["crm", "aiSummary", customerId],
    queryFn: () =>
      client
        .get(`/admin/customers/${customerId}/ai-summary`)
        .then((r) => r.data),
    enabled: Boolean(customerId),
    // Refetch every 3s while generating. The refetchInterval callback receives
    // the current query (v5) so we can read status and stop polling when done.
    refetchInterval: (query) => {
      const data = query.state.data as AiSummaryResult | undefined;
      if (!data || data.status !== "generating") return false;
      const elapsed = Date.now() - (query.state.dataUpdatedAt ?? Date.now());
      if (elapsed > POLL_CAP_MS) return false;
      return POLL_INTERVAL_MS;
    },
    // Keep the summary visible while polling so the UI doesn't flash empty.
    placeholderData: (prev) => prev,
  });
}

/**
 * Force-triggers a fresh regeneration. The endpoint returns status='generating'
 * immediately; useAiSummary's poll picks up the fresh text when the background
 * model call completes.
 */
export function useRegenerateAiSummary(customerId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      client
        .post(`/admin/customers/${customerId}/ai-summary/regenerate`)
        .then((r) => r.data as AiSummaryResult),
    onSuccess: (data) => {
      // Seed the cache with the generating marker so the poll fires right away
      qc.setQueryData(["crm", "aiSummary", customerId], data);
      qc.invalidateQueries({ queryKey: ["crm", "aiSummary", customerId] });
    },
  });
}
